// src/components/AdminDashboard/sections/FeedbackManagementSection.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { Typography, Tooltip, CircularProgress, Box, Table, TableHead, TableRow, TableBody, TableCell, Button, Paper } from '@mui/material';
import FeedbackIcon from '@mui/icons-material/Feedback';
import DeleteIcon from '@mui/icons-material/Delete';

const FeedbackManagementSection = ({ feedbackList, loadingFeedback, feedbackError, handleDeleteFeedback }) => (
  <Paper className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 mb-8 shadow-admin-card border border-accent-teal/10 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-admin-card-hover hover:border-accent-teal/20 relative overflow-hidden before:content-[''] before:absolute before:top-0 before:left-0 before:right-0 before:h-0.5 before:bg-gradient-to-r before:from-accent-teal before:to-accent-orange before:opacity-0 before:transition-opacity before:duration-300 hover:before:opacity-100">
    <div className="flex justify-between items-center mb-6 pb-4 border-b-2 border-accent-teal/10">
      <Box display="flex" alignItems="center" gap={1}>
        <FeedbackIcon className="text-accent-teal" />
        <Typography variant="h6" className="text-accent-grey font-bold text-xl m-0">
          Feedback Management
        </Typography>
      </Box>
    </div>
    
    <div className="overflow-x-auto w-full mb-6 rounded-xl shadow-lg bg-white">
      <Table className="min-w-[700px] [&_.MuiTableHead-root]:bg-gradient-to-r [&_.MuiTableHead-root]:from-accent-teal [&_.MuiTableHead-root]:to-accent-grey [&_.MuiTableCell-head]:text-white [&_.MuiTableCell-head]:font-bold [&_.MuiTableCell-head]:text-sm [&_.MuiTableCell-head]:uppercase [&_.MuiTableCell-head]:tracking-wider [&_.MuiTableCell-head]:border-none [&_.MuiTableCell-head]:p-4 [&_.MuiTableBody-root_.MuiTableRow-root]:transition-colors [&_.MuiTableBody-root_.MuiTableRow-root]:duration-200 [&_.MuiTableBody-root_.MuiTableRow-root:hover]:bg-accent-teal/5 [&_.MuiTableBody-root_.MuiTableRow-root:nth-of-type(even)]:bg-accent-teal/[0.02] [&_.MuiTableCell-body]:border-b [&_.MuiTableCell-body]:border-accent-grey/10 [&_.MuiTableCell-body]:text-accent-grey [&_.MuiTableCell-body]:font-medium [&_.MuiTableCell-body]:p-4">
        <TableHead>
          <TableRow>
            <TableCell className="text-accent-grey font-bold text-sm uppercase tracking-wider">Username</TableCell>
            <TableCell className="text-accent-grey font-bold text-sm uppercase tracking-wider">Email</TableCell>
            <TableCell className="text-accent-grey font-bold text-sm uppercase tracking-wider">Content</TableCell>
            <TableCell className="text-accent-grey font-bold text-sm uppercase tracking-wider">Rating</TableCell>
            <TableCell className="text-accent-grey font-bold text-sm uppercase tracking-wider">Timestamp</TableCell>
            <TableCell className="text-accent-grey font-bold text-sm uppercase tracking-wider">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loadingFeedback ? (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <div className="flex justify-center items-center p-10">
                  <CircularProgress className="text-accent-teal" />
                </div>
              </TableCell>
            </TableRow>
          ) : (
            feedbackList.map((feedback) => (
              <TableRow key={feedback.id} className="hover:bg-accent-teal/5">
                <TableCell>{feedback.username}</TableCell>
                <TableCell>{feedback.email}</TableCell>
                <TableCell>{feedback.feedbackContent || 'N/A'}</TableCell>
                <TableCell>{feedback.rating || 'N/A'}</TableCell>
                <TableCell>
                  {feedback.timestamp
                    ? new Date(feedback.timestamp).toLocaleString()
                    : 'N/A'}
                </TableCell>
                <TableCell>
                  <Tooltip title="Delete Feedback">
                    <Button
                      variant="contained"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDeleteFeedback(feedback.id)}
                      className="bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg px-4 py-2 font-semibold normal-case shadow-md transition-all duration-300 hover:from-red-600 hover:to-red-700 hover:-translate-y-0.5 hover:shadow-lg"
                    >
                      Delete
                    </Button>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      {!loadingFeedback && feedbackList.length === 0 && !feedbackError && (
        <div className="text-center text-accent-grey mt-8 p-10 bg-gradient-to-br from-accent-teal/5 to-accent-orange/5 rounded-xl border-2 border-dashed border-accent-teal/30 text-lg font-medium">
          No feedback available.
        </div>
      )}
      {feedbackError && (
        <div className="text-center text-red-500 mt-5 p-4 bg-gradient-to-br from-red-500/10 to-red-600/10 rounded-lg border-l-4 border-red-500 font-semibold">
          {feedbackError}
        </div>
      )}
    </div>
  </Paper>
);

FeedbackManagementSection.propTypes = {
  feedbackList: PropTypes.array.isRequired,
  loadingFeedback: PropTypes.bool.isRequired,
  feedbackError: PropTypes.string,
  handleDeleteFeedback: PropTypes.func.isRequired
};

export default FeedbackManagementSection;
